import { media } from '@goalpost/content';
import { useTheme } from '../hooks/useTheme.tsx';

interface MediaVideoProps {
  // Base media id without the theme suffix or extension, e.g.
  // `overview-approval-rules-software`. Resolves to the no-burn
  // `{base}-{light|dark}-cc.mp4` cut and its matching `.vtt` caption track
  // through the media manifest.
  base: string;
  // Accessible description of what the video shows.
  label: string;
  // Class for the wrapping `<figure>`. Defaults to the docs figure frame; a
  // caller in a different layout passes its own.
  className?: string;
}

// A theme-matched product video resolved from the media manifest in
// `@goalpost/content`. Renders nothing until the asset has been synced (the
// manifest is empty on a fresh checkout), so a page can embed a video before
// its bytes exist and light up automatically once `marketing:asset:push` runs.
export function MediaVideo({ base, label, className = 'mkt-doc-figure' }: MediaVideoProps) {
  const { theme } = useTheme();
  const preferred = theme === 'graphite' ? 'dark' : 'light';
  const fallback = preferred === 'dark' ? 'light' : 'dark';

  // The no-burn "-cc" cut (the open-caption burn-in switched off), paired with
  // the .vtt <track> below so captions are a real toggle rather than painted
  // into the frames — see marketing/README.md. Prefer the theme-matched render;
  // fall back to the other variant so a partial sync (only one theme uploaded)
  // still shows the video rather than an empty gap. Both are null until the
  // manifest carries the asset.
  const preferredVideo = media(`${base}-${preferred}-cc.mp4`);
  const video = preferredVideo ?? media(`${base}-${fallback}-cc.mp4`);
  if (!video) return null;

  const variant = preferredVideo ? preferred : fallback;
  const captions = media(`${base}-${variant}.vtt`);
  // The poster the marketing render emits beside the cut — a downscaled capture
  // screenshot of the video's settled payoff beat (the target's `poster`; see
  // marketing render.ts / targets.ts). Shown before play so the embed doesn't
  // open on the video's own first frame (a white title-card fade-in, which some
  // browsers don't paint at all before play). null until synced, so the element
  // simply has no poster until then.
  const poster = media(`${base}-${variant}-poster.webp`);

  return (
    <figure className={className}>
      {/*
        The video and its caption track are served from the media subdomain — a
        different origin than the app. A cross-origin text track only loads when
        the media element opts into CORS via `crossOrigin`, so it's set exactly
        when there's a track to load. That also turns the video fetch itself into
        a CORS request, so the media host must return `Access-Control-Allow-Origin`
        for both — see docs/media-storage.md. A video with no synced `.vtt` keeps a
        plain fetch and no CORS dependency.
      */}
      <video
        controls
        preload="metadata"
        playsInline
        crossOrigin={captions ? 'anonymous' : undefined}
        aria-label={label}
        poster={poster?.url}
      >
        <source src={video.url} type={video.contentType} />
        {captions && (
          <track
            kind="captions"
            src={captions.url}
            srcLang="en"
            label="English"
            default
          />
        )}
      </video>
    </figure>
  );
}
